import React, { useState } from 'react';
import SectionHeading from '../components/SectionHeading';
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
const ApplyPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    militaryStatus: '',
    businessStage: '',
    businessType: '',
    annualRevenue: '',
    challenges: '',
    goals: '',
    commitment: '',
    referralSource: ''
  });
  const [expandedFaq, setExpandedFaq] = useState(null);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real application, this would submit the form data to a server
    alert('Application submitted! We will contact you soon.');
  };
  const toggleFaq = index => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };
  return <div className="w-full" data-id="element-674">
      {/* Hero Section */}
      <section className="bg-navy text-white py-16" data-id="element-675">
        <div className="container-custom text-center" data-id="element-676">
          <h1 className="text-4xl md:text-5xl font-bold font-montserrat mb-6" data-id="element-677">
            Apply to Operation Launchpad
          </h1>
          <p className="text-xl max-w-3xl mx-auto" data-id="element-678">
            Take the first step toward building your $1M+ business.
          </p>
        </div>
      </section>
      {/* Application Process Overview */}
      <section className="section-padding bg-lightGray" data-id="element-679">
        <div className="container-custom" data-id="element-680">
          <SectionHeading title="Application Process" subtitle="Our selection process is designed to identify committed entrepreneurs who are ready to put in the work required for success." data-id="element-681" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-682">
            <ProcessCard number="1" title="Submit Application" description="Complete the application form below with information about yourself and your business goals." data-id="element-683" />
            <ProcessCard number="2" title="Initial Review" description="Our team will review your application to determine if you're a potential fit for our program." data-id="element-684" />
            <ProcessCard number="3" title="Interview" description="Selected applicants will be invited for a virtual interview to discuss their goals and commitment." data-id="element-685" />
            <ProcessCard number="4" title="Assessment" description="We'll work with you to assess your current situation and develop a preliminary growth plan." data-id="element-686" />
            <ProcessCard number="5" title="Acceptance" description="Qualified candidates will receive an offer to join the Operation Launchpad program." data-id="element-687" />
            <ProcessCard number="6" title="Onboarding" description="Begin your journey with orientation and implementation of initial business systems." data-id="element-688" />
          </div>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-16" data-id="element-689">
            <h3 className="text-2xl font-bold text-navy mb-6 font-montserrat text-center" data-id="element-690">
              What We Look For
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-691">
              <div data-id="element-692">
                <h4 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-693">
                  Ideal Candidates
                </h4>
                <ul className="space-y-3" data-id="element-694">
                  {['Aspiring entrepreneurs with a clear business vision', 'Existing small business owners looking to scale', 'Individuals with a strong work ethic and commitment to growth', 'Those willing to follow proven systems and processes', 'People who value mentorship and community support', 'Self-starters ready to take their business to the next level'].map((item, index) => <li key={index} className="flex items-start" data-id="element-695">
                      <CheckCircle className="text-gold mr-3 flex-shrink-0 mt-1" size={20} data-id="element-696" />
                      <span data-id="element-697">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div data-id="element-698">
                <h4 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-699">
                  Our Partnership Model
                </h4>
                <p className="mb-4" data-id="element-700">
                  Operation Launchpad offers a unique partnership model:
                </p>
                <ul className="space-y-3" data-id="element-701">
                  {['No large upfront fees for our programs and systems', 'We take a vested interest in your success through future billing', "This ensures we're truly committed to helping you succeed", 'We provide ongoing support throughout your growth journey', 'Our success is directly tied to your business performance', "We're building long-term relationships, not short-term transactions"].map((item, index) => <li key={index} className="flex items-start" data-id="element-702">
                      <CheckCircle className="text-gold mr-3 flex-shrink-0 mt-1" size={20} data-id="element-703" />
                      <span data-id="element-704">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Application Form */}
      <section className="section-padding" data-id="element-705">
        <div className="container-custom" data-id="element-706">
          <SectionHeading title="Application Form" subtitle="Please complete the form below to apply for the Operation Launchpad program." data-id="element-707" />
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto" data-id="element-708">
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-709">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-710">
                <div data-id="element-711">
                  <label htmlFor="firstName" className="block text-darkGray font-medium mb-2" data-id="element-712">
                    First Name *
                  </label>
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-713" />
                </div>
                <div data-id="element-714">
                  <label htmlFor="lastName" className="block text-darkGray font-medium mb-2" data-id="element-715">
                    Last Name *
                  </label>
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-716" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-717">
                <div data-id="element-718">
                  <label htmlFor="email" className="block text-darkGray font-medium mb-2" data-id="element-719">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-720" />
                </div>
                <div data-id="element-721">
                  <label htmlFor="phone" className="block text-darkGray font-medium mb-2" data-id="element-722">
                    Phone Number *
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-723" />
                </div>
              </div>
              <div data-id="element-724">
                <label htmlFor="militaryStatus" className="block text-darkGray font-medium mb-2" data-id="element-725">
                  Background
                </label>
                <select id="militaryStatus" name="militaryStatus" value={formData.militaryStatus} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-726">
                  <option value="" data-id="element-727">Select an option</option>
                  <option value="corporate" data-id="element-728">Corporate Background</option>
                  <option value="entrepreneur" data-id="element-729">Prior Entrepreneur</option>
                  <option value="student" data-id="element-730">Recent Graduate</option>
                  <option value="other" data-id="element-731">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-732">
                <div data-id="element-733">
                  <label htmlFor="businessStage" className="block text-darkGray font-medium mb-2" data-id="element-734">
                    Business Stage *
                  </label>
                  <select id="businessStage" name="businessStage" value={formData.businessStage} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-735">
                    <option value="" data-id="element-736">Select an option</option>
                    <option value="idea" data-id="element-737">Business Idea (Pre-launch)</option>
                    <option value="startup" data-id="element-738">Startup (0-2 years)</option>
                    <option value="established" data-id="element-739">Established (2+ years)</option>
                    <option value="growing" data-id="element-740">Growing Business</option>
                  </select>
                </div>
                <div data-id="element-741">
                  <label htmlFor="businessType" className="block text-darkGray font-medium mb-2" data-id="element-742">
                    Business Type/Industry *
                  </label>
                  <select id="businessType" name="businessType" value={formData.businessType} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-743">
                    <option value="" data-id="element-744">Select an option</option>
                    <option value="tech" data-id="element-745">Technology/SaaS</option>
                    <option value="retail" data-id="element-746">Retail/E-commerce</option>
                    <option value="service" data-id="element-747">Professional Services</option>
                    <option value="construction" data-id="element-748">Construction/Trades</option>
                    <option value="food" data-id="element-749">Food & Beverage</option>
                    <option value="health" data-id="element-750">Health & Wellness</option>
                    <option value="manufacturing" data-id="element-751">Manufacturing</option>
                    <option value="other" data-id="element-752">
                      Other (specify in challenges field)
                    </option>
                  </select>
                </div>
              </div>
              <div data-id="element-753">
                <label htmlFor="annualRevenue" className="block text-darkGray font-medium mb-2" data-id="element-754">
                  Current Annual Revenue (if applicable)
                </label>
                <select id="annualRevenue" name="annualRevenue" value={formData.annualRevenue} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-755">
                  <option value="" data-id="element-756">Select an option</option>
                  <option value="pre-revenue" data-id="element-757">Pre-revenue</option>
                  <option value="0-100k" data-id="element-758">$0 - $100,000</option>
                  <option value="100k-250k" data-id="element-759">$100,000 - $250,000</option>
                  <option value="250k-500k" data-id="element-760">$250,000 - $500,000</option>
                  <option value="500k-1m" data-id="element-761">$500,000 - $1,000,000</option>
                  <option value="1m+" data-id="element-762">$1,000,000+</option>
                </select>
              </div>
              <div data-id="element-763">
                <label htmlFor="challenges" className="block text-darkGray font-medium mb-2" data-id="element-764">
                  What are your biggest business challenges right now? *
                </label>
                <textarea id="challenges" name="challenges" value={formData.challenges} onChange={handleChange} required rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-765"></textarea>
              </div>
              <div data-id="element-766">
                <label htmlFor="goals" className="block text-darkGray font-medium mb-2" data-id="element-767">
                  What are your primary business goals for the next 1-2 years? *
                </label>
                <textarea id="goals" name="goals" value={formData.goals} onChange={handleChange} required rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-768"></textarea>
              </div>
              <div data-id="element-769">
                <label htmlFor="commitment" className="block text-darkGray font-medium mb-2" data-id="element-770">
                  How committed are you to putting in the work required to grow
                  your business? *
                </label>
                <select id="commitment" name="commitment" value={formData.commitment} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-771">
                  <option value="" data-id="element-772">Select an option</option>
                  <option value="very" data-id="element-773">
                    Very committed - this is my top priority
                  </option>
                  <option value="somewhat" data-id="element-774">
                    Somewhat committed - I have other obligations
                  </option>
                  <option value="exploring" data-id="element-775">
                    Exploring options - not fully committed yet
                  </option>
                </select>
              </div>
              <div data-id="element-776">
                <label htmlFor="referralSource" className="block text-darkGray font-medium mb-2" data-id="element-777">
                  How did you hear about Operation Launchpad?
                </label>
                <select id="referralSource" name="referralSource" value={formData.referralSource} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-778">
                  <option value="" data-id="element-779">Select an option</option>
                  <option value="search" data-id="element-780">Online Search</option>
                  <option value="social" data-id="element-781">Social Media</option>
                  <option value="referral" data-id="element-782">
                    Referral from a Friend/Colleague
                  </option>
                  <option value="event" data-id="element-783">Event or Conference</option>
                  <option value="other" data-id="element-784">Other</option>
                </select>
              </div>
              <div className="pt-4" data-id="element-785">
                <button type="submit" className="btn-primary w-full md:w-auto" data-id="element-786">
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="section-padding bg-lightGray" data-id="element-787">
        <div className="container-custom" data-id="element-788">
          <SectionHeading title="Frequently Asked Questions" subtitle="Get answers to common questions about our application process and program." data-id="element-789" />
          <div className="max-w-4xl mx-auto" data-id="element-790">
            {faqs.map((faq, index) => <div key={index} className="mb-4" data-id="element-791">
                <button className="flex justify-between items-center w-full bg-white p-4 rounded-lg shadow-md hover:bg-gray-50 transition duration-300" onClick={() => toggleFaq(index)} data-id="element-792">
                  <span className="font-bold text-navy text-left" data-id="element-793">
                    {faq.question}
                  </span>
                  {expandedFaq === index ? <ChevronUp size={20} data-id="element-794" /> : <ChevronDown size={20} data-id="element-795" />}
                </button>
                {expandedFaq === index && <div className="bg-white p-4 rounded-b-lg shadow-md mt-1" data-id="element-796">
                    <p className="text-darkGray" data-id="element-797">{faq.answer}</p>
                  </div>}
              </div>)}
          </div>
        </div>
      </section>
    </div>;
};
const ProcessCard = ({
  number,
  title,
  description
}) => {
  return <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-gold" data-id="element-798">
      <div className="bg-navy text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold mb-4" data-id="element-799">
        {number}
      </div>
      <h3 className="text-xl font-bold text-navy mb-3 font-montserrat" data-id="element-800">
        {title}
      </h3>
      <p className="text-darkGray" data-id="element-801">{description}</p>
    </div>;
};
const faqs = [{
  question: 'Who can apply to the program?',
  answer: "Our program is open to all committed entrepreneurs across various industries. We evaluate each application based on the individual's commitment, goals, and fit with our program."
}, {
  question: 'How much does the program cost?',
  answer: "Unlike traditional business programs, we don't charge large upfront fees. Instead, we take a vested interest in your success through future billing once your business is generating revenue. This ensures we're truly invested in your growth - we only win when you win."
}, {
  question: 'How long is the program?',
  answer: 'Our program is designed to be a long-term partnership. The initial intensive phase typically lasts 6-12 months, followed by ongoing support as your business grows. Our goal is to help you reach $1M+ in annual revenue, which may take 2-3 years depending on your starting point.'
}, {
  question: 'What if I already have an established business?',
  answer: 'Our program is designed to help businesses at all stages. If you already have an established business but want to scale to the next level, we can provide the systems, strategies, and support to help you grow more efficiently and profitably.'
}, {
  question: 'What kind of support will I receive?',
  answer: 'Our comprehensive program includes business systems implementation, marketing and sales strategies, financial management guidance, leadership development, technology integration, and ongoing mentorship from successful entrepreneurs in your industry.'
}, {
  question: 'How selective is the application process?',
  answer: "We carefully select participants to ensure they're a good fit for our program and have the commitment needed for success. While we don't focus on traditional qualifications like education or experience, we do look for individuals with strong work ethic, coachability, and determination."
}];
export default ApplyPage;